// ===== Saved data: versions and migrations (localStorage 'mekaniko.v<N>') =====
// MIGRATIONS[i] lifts data from version i+1 to i+2. New migrations go at the end.
// The old key is left in place, so an older version of the game can still read its data.
// The file has the old Danish keys on purpose (they are the keys in old saves).
// Saved data is JSON from localStorage; the profiles' fields are checked on use
type SaveData = { profiles?: ({ intros?: Record<string, unknown>; prog?: unknown; fails?: unknown } | null)[]; [k: string]: unknown };

export const SAVE = (() => {
  const PREFIX = 'mekaniko.v';

  // v1 -> v2 (S2, English names): profiles[].intros is keyed by part/object type
  const V1_INTROS: Record<string, string> = {
    pude: 'pad', banan: 'banana', maling: 'paint', vippe: 'seesaw', knap: 'button', trisse: 'pulley',
    opvask_bakke: 'dishes_tray', opvask_svamp: 'dishes_sponge', opvask_slev: 'dishes_ladle',
    sokker_elastik: 'socks_elastic', sokker_klemme: 'socks_clothespin', sokker_uldsok: 'socks_wool_sock',
    tand_vand: 'toothbrush_water', tand_boble: 'toothbrush_bubbles', tand_svamp: 'toothbrush_sponge', tand_saebe: 'toothbrush_soap',
    katte_bog: 'cats_book', katte_kat: 'cats_cat', katte_pendul: 'cats_pendulum',
    hons_vippe: 'henhouse_seesaw', hons_fjer: 'henhouse_feather', hons_hone: 'henhouse_hen', hons_saek: 'henhouse_sack',
    muldvarp_hejs: 'moles_hoist', muldvarp_vogn: 'moles_cart', muldvarp_saek: 'moles_sack',
    pingvin_is: 'penguins_ice', pingvin_sne: 'penguins_snow', pingvin_pingvin: 'penguins_penguin', pingvin_grus: 'penguins_gravel',
    pizza_hjul: 'pizza_wheel', pizza_storhjul: 'pizza_big_wheel',
    spaghetti_pendul: 'spaghetti_pendulum', spaghetti_saks: 'spaghetti_scissors', spaghetti_bro: 'spaghetti_bridge', spaghetti_langpendul: 'spaghetti_long_pendulum',
    gele_klods: 'jelly_block', gele_skumfidus: 'jelly_marshmallow', gele_bolsje: 'jelly_candy', gele_tung: 'jelly_heavy', gele_let: 'jelly_light',
    rum_raket: 'space_rocket', rum_maane: 'space_moon', rum_asteroide: 'space_asteroid', rum_skjold: 'space_shield', rum_planet: 'space_planet',
    robot_ledning: 'robot_wire', robot_kontakt: 'robot_switch', robot_snor: 'robot_string', robot_batteri: 'robot_battery', robot_hikke: 'robot_hiccup',
    loft_magnet: 'attic_magnet', loft_ur: 'attic_timer',
    hav_vand: 'ocean_water', hav_boble: 'ocean_bubbles', hav_snegl: 'ocean_conch', hav_ballon: 'ocean_balloon', hav_flyder: 'ocean_float',
    hav_vandmand: 'ocean_jellyfish', hav_strom: 'ocean_current',
    omvendt_pil: 'upsidedown_arrow', omvendt_sky: 'upsidedown_cloud', omvendt_zone: 'upsidedown_zone',
  };

  // v2 -> v3 (S4, stable level ids): FROZEN copy of the level order in v2 (world, ids in order).
  // prog/fails were keyed by the global level number in this order; now by '<world>/<id>'.
  // NEVER edit the list – it describes old saved data, not the game today.
  const V2_LEVELS: [string, string][] = [
    ['breakfast', 'first-roll bounce banana seesaw paint-bucket toaster cut-the-rope three-peas window pulley chain-reaction morning-bell'],
    ['dishes', 'first-roll drip other-way pour-more tea-towel sponge mirror-tray zigzag speed-up ladle-hop speed-to-hop over-the-sink long-way glove masterpiece'],
    ['socks', 'first-hop slingshot wool-sock long-shot short-shot out-of-the-hole stop-the-hop crooked-hop over-the-dresser double-hop soft-landing bank-shot pair-of-socks ball-of-yarn master'],
    ['toothbrush', 'splash bubbles sponge-bridge sponge-or-soap boat two-bubbles tap two-holes out-of-the-tub who-carries which-way bridge-and-bubbles lift-the-boat hop-in-the-bath master'],
    ['cats', 'book-topples wake-the-cat toy-mouse big-or-small gap-in-the-row mouse-starts cat-jumps right-way stairs mouse-and-book far-away two-cats off-the-shelf hop-and-topple master-chain'],
    ['henhouse', 'thump feather hen far-out middle heavy-enough place-the-seesaw flip-the-seesaw over-the-fence two-eggs flip-and-move under-the-roof egg-tray pumpkin master'],
    ['moles', 'up-the-lift mine-cart rope-pulls heavy-or-light into-the-deep two-crystals uphill follow-the-rope hatch up-or-down coal-on-the-lift two-floors lift-and-hatch mushroom-hop master-mine'],
    ['penguins', 'slide snow-brakes belly-penguin brake far-away gravel penguin-run flat-or-slope bridge-and-brake three-chicks penguin-path long-trip skiing seal master-penguin'],
    ['pizza', 'gear other-way dough-roller left-or-right reversed fast-belt slow-belt two-wheels big-jump roller-turns stuck oven over-the-wall dough-lump pizza-master'],
    ['spaghetti', 'tomato-push snip rope-bridge long-string high-swing let-go-mid-swing too-far which-way up-and-over pass-it-on push-over-the-bridge cheese-catapult let-go-and-bridge push-bridge-hop master-chef'],
    ['jelly', 'wiggle stop clack high-hop heavy-ball soft-landing angle light-push double-hop small-hop heavy-vs-heavy push-and-hop clack-and-stop push-hop-stop master'],
    ['space', 'moon-hop rocket moon-pulls asteroid aim-up not-too-close no-air around-the-planet moon-trampoline seesaw button over-the-wall two-rockets hop-and-hatch master'],
    ['robot', 'gate switch belt string-or-wire battery all-the-way-round hiccup-robot which-wire battery-and-wire copper-ball robot-and-fan switch-and-wire lift nut-and-spring master-workshop'],
    ['attic', 'click uphill hold-and-release far-away through-the-book wooden-ball swing button onto-the-suitcase stopper iron fish-it-up stairs tip-and-click master'],
    ['ocean', 'sink bubbles current bubble-balloon float countercurrent jellyfish bubble-pump empty-the-float bubble-and-current steer-the-balloon hop-and-bubbles float-and-current balloon-journey master'],
    ['upsidedown', 'up arrow cloud flip-zone short-trip pot down-again hop-and-flip tip-and-zone snip-and-flip honey pulley into-the-basket toaster big-machine'],
  ];
  const V2_KEYS = ([] as string[]).concat(...V2_LEVELS.map(([w, ids]) => ids.split(' ').map(id => w + '/' + id)));
  // { '12': v } -> { 'dishes/first-roll': v }. Unknown numbers are dropped; keys that are already ids are kept.
  function byId(m: unknown) {
    if (!m || typeof m !== 'object') return m;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(m)) {
      if (k.includes('/')) out[k] = v;
      else if (/^\d+$/.test(k) && V2_KEYS[+k]) out[V2_KEYS[+k]] = v;
    }
    return out;
  }

  const MIGRATIONS = [
    function v1to2(d: SaveData) {
      for (const p of d.profiles || []) {
        if (!p || !p.intros) continue;
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(p.intros)) out[V1_INTROS[k] || k] = v;
        p.intros = out;
      }
      return d;
    },
    function v2to3(d: SaveData) {
      for (const p of d.profiles || []) {
        if (!p) continue;
        if (p.prog) p.prog = byId(p.prog);
        if (p.fails) p.fails = byId(p.fails);
      }
      return d;
    },
  ];
  const VERSION = MIGRATIONS.length + 1;

  function migrate(d: SaveData, from: number) {
    for (let v = from; v < VERSION; v++) d = MIGRATIONS[v - 1](d);
    return d;
  }

  // Read the newest saved version and migrate it up. from = the version that was found (0 = none).
  function load(storage: Pick<Storage, 'getItem'>): { d: SaveData | null; from: number } {
    for (let v = VERSION; v >= 1; v--) {
      let raw = null;
      try { raw = storage.getItem(PREFIX + v); } catch (e) {}
      if (raw == null) continue;
      let d = null;
      try { d = JSON.parse(raw); } catch (e) {}
      return { d: d && typeof d === 'object' ? migrate(d, v) : null, from: v };
    }
    return { d: null, from: 0 };
  }

  return { VERSION, KEY: PREFIX + VERSION, PREFIX, MIGRATIONS, V1_INTROS, V2_KEYS, migrate, load };
})();
